import { useState } from "react";
import { Alert, Pressable, ScrollView, Text, View } from "react-native";
import { ChevronLeft } from "lucide-react-native";
import { useNavigation } from "@react-navigation/native";
import {
  colors,
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
  spacing,
  typography,
} from "@louise/moon-ui/theme/tokens";
import type { VerificationStatus } from "@src/packages/coverage/hooks/useCoverage";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import EmployerBenefitsCard from "../YouScreen/sections/EmployerBenefitsCard";
import InsuranceCard from "../YouScreen/sections/InsuranceCard";

const SAMPLE_INSURANCE = {
  provider: "Sample Health Plan",
  planName: "Preferred PPO",
  memberId: "LH-4827-1936",
};
const SECOND_SAMPLE_INSURANCE = {
  provider: "Coastal Health",
  planName: "Choice HMO",
  memberId: "LH-9153-8402",
};

const PREVIEW_STATES: {
  id: string;
  label: string;
  hasInsurance: boolean;
  verificationStatus: VerificationStatus;
  isActive: boolean;
  isInsuranceLoading?: boolean;
  hasAdditionalInsurance: boolean;
  hasEmployerBenefits: boolean | null;
  employerBenefit: string | null;
}[] = [
  {
    id: "loading",
    label: "Loading",
    hasInsurance: false,
    verificationStatus: "unverified",
    isActive: true,
    isInsuranceLoading: true,
    hasAdditionalInsurance: false,
    hasEmployerBenefits: null,
    employerBenefit: null,
  },
  {
    id: "unfilled",
    label: "Unfilled",
    hasInsurance: false,
    verificationStatus: "unverified",
    isActive: true,
    hasAdditionalInsurance: false,
    hasEmployerBenefits: null,
    employerBenefit: null,
  },
  {
    id: "verified",
    label: "Verified",
    hasInsurance: true,
    verificationStatus: "verified",
    isActive: true,
    hasAdditionalInsurance: false,
    hasEmployerBenefits: true,
    employerBenefit: "Carrot",
  },
  {
    id: "pending",
    label: "Checking",
    hasInsurance: true,
    verificationStatus: "pending",
    isActive: true,
    hasAdditionalInsurance: false,
    hasEmployerBenefits: null,
    employerBenefit: null,
  },
  {
    id: "unverified",
    label: "Needs review",
    hasInsurance: true,
    verificationStatus: "unverified",
    isActive: true,
    hasAdditionalInsurance: false,
    hasEmployerBenefits: false,
    employerBenefit: null,
  },
  {
    id: "expired",
    label: "Expired",
    hasInsurance: true,
    verificationStatus: "unverified",
    isActive: false,
    hasAdditionalInsurance: false,
    hasEmployerBenefits: true,
    employerBenefit: "Progyny",
  },
  {
    id: "two-plans",
    label: "Two plans",
    hasInsurance: true,
    verificationStatus: "verified",
    isActive: true,
    hasAdditionalInsurance: true,
    hasEmployerBenefits: true,
    employerBenefit: "Carrot",
  },
];
const DEFAULT_PREVIEW_STATE = PREVIEW_STATES[0]!;

export default function DevCoverageCardFixtureScreen() {
  const navigation = useNavigation();
  const [selectedStateId, setSelectedStateId] = useState("unfilled");
  const selectedState =
    PREVIEW_STATES.find((state) => state.id === selectedStateId) ??
    DEFAULT_PREVIEW_STATE;
  const previewPolicies = selectedState.hasInsurance
    ? [
        SAMPLE_INSURANCE,
        ...(selectedState.hasAdditionalInsurance
          ? [SECOND_SAMPLE_INSURANCE]
          : []),
      ]
    : [{ provider: null, planName: null, memberId: null }];

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          padding: spacing.lg,
          paddingTop: SCREEN_PADDING_TOP + spacing.lg,
          gap: spacing.lg,
          maxWidth: 390,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back"
          onPress={() => navigation.goBack()}
          style={{
            alignSelf: "flex-start",
            flexDirection: "row",
            alignItems: "center",
            gap: spacing.xs,
          }}
        >
          <ChevronLeft size={20} color={colors.text} />
          <Text
            style={{
              color: colors.text,
              fontFamily: typography.families.sansMedium,
              fontSize: typography.sizes.md,
            }}
          >
            Back
          </Text>
        </Pressable>

        <View style={{ gap: spacing.xs }}>
          <Text
            style={{
              color: colors.text,
              fontFamily: typography.families.sansBold,
              fontSize: typography.sizes["2xl"],
              letterSpacing: -0.3,
            }}
          >
            Coverage card directions
          </Text>
          <Text
            style={{
              color: colors.textMid,
              fontFamily: typography.families.sans,
              fontSize: typography.sizes.base,
              lineHeight: 20,
            }}
          >
            The textured coverage cards from Coverage & Benefits in each
            verification state. This fixture uses sample data and never reads or
            saves your account.
          </Text>
        </View>

        <View style={{ gap: spacing.xs }}>
          <Text
            style={{
              color: colors.textMid,
              fontFamily: typography.families.sansMedium,
              fontSize: typography.sizes.sm,
            }}
          >
            Coverage cards
          </Text>
          {previewPolicies.map((policy, index) => (
            <InsuranceCard
              key={`textured-${policy.memberId ?? "empty"}`}
              insuranceInfo={policy}
              hasInsurance={selectedState.hasInsurance}
              loading={selectedState.isInsuranceLoading}
              isActive={selectedState.isActive}
              verificationStatus={selectedState.verificationStatus}
              onAdd={() =>
                Alert.alert(
                  "Preview only",
                  "This sample card cannot save insurance details.",
                )
              }
              onEdit={() =>
                Alert.alert(
                  "Preview only",
                  index === 0
                    ? "This card uses sample coverage data, so editing is disabled in the fixture."
                    : "This second plan demonstrates the repeated policy-card layout.",
                )
              }
            />
          ))}

          <EmployerBenefitsCard
            hasEmployerBenefits={selectedState.hasEmployerBenefits}
            employerBenefit={selectedState.employerBenefit}
            onPress={() =>
              Alert.alert(
                "Preview only",
                "This sample card cannot save employer benefits.",
              )
            }
          />
        </View>

        <View style={[GLASS_CARD_STYLE, { padding: spacing.md, gap: 10 }]}>
          <Text
            style={{
              color: colors.text,
              fontFamily: typography.families.sansMedium,
              fontSize: typography.sizes.md,
            }}
          >
            Preview states
          </Text>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            {PREVIEW_STATES.map((state) => {
              const selected = state.id === selectedState.id;
              return (
                <Pressable
                  key={state.id}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  onPress={() => setSelectedStateId(state.id)}
                  style={{
                    borderRadius: 999,
                    borderWidth: 1,
                    borderColor: selected ? colors.warm : colors.border,
                    backgroundColor: selected
                      ? `${colors.warm}14`
                      : "transparent",
                    paddingHorizontal: 11,
                    paddingVertical: 7,
                  }}
                >
                  <Text
                    style={{
                      color: selected ? colors.warm : colors.textMid,
                      fontFamily: typography.families.sansMedium,
                      fontSize: typography.sizes.sm,
                    }}
                  >
                    {state.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>

        <View style={[GLASS_CARD_STYLE, { padding: spacing.md, gap: 6 }]}>
          <Text
            style={{
              color: colors.text,
              fontFamily: typography.families.sansMedium,
              fontSize: typography.sizes.md,
            }}
          >
            What these cards communicate
          </Text>
          <Text
            style={{
              color: colors.textMid,
              fontFamily: typography.families.sans,
              fontSize: typography.sizes.base,
              lineHeight: 20,
            }}
          >
            Insurance activity and benefits verification are intentionally
            separate. Employer fertility benefits are also separate from health
            insurance, since they may be administered through a workplace
            program. Member IDs stay masked in this overview; Edit opens the
            full form in the real Coverage & Benefits flow.
          </Text>
        </View>
      </ScrollView>
    </GlassScreenLayout>
  );
}
